import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { Gift, X } from 'lucide-react';
import { MultiMethodLoginForm } from '../../components/pages/login/sections/FormSection/MultiMethodLoginForm';
import type { LoginStep } from '../../components/pages/login/sections/FormSection/multiMethod/types';
import { readStoredReferralCode } from '@lib/api/ashrafie/referralAttribution';
import { cn } from '../../lib/utils';

interface AuthSheetProps {
  isOpen: boolean;
  reason?: string;
  onClose: () => void;
  onAuthenticated: () => void;
}

const SHEET_SPRING = {
  type: 'spring' as const,
  damping: 32,
  stiffness: 360,
  mass: 0.8
};

export const AuthSheet: React.FC<AuthSheetProps> = ({
  isOpen,
  reason,
  onClose,
  onAuthenticated
}) => {
  const { t } = useTranslation('auth');
  const [isDesktop, setIsDesktop] = useState(false);
  const [currentStep, setCurrentStep] = useState<LoginStep>('email');

  const handleStepChange = useCallback((step: LoginStep, _newUser: boolean) => {
    setCurrentStep(step);
  }, []);

  // Reset step when sheet closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep('email');
    }
  }, [isOpen]);

  // Step-aware header copy
  const headerTitle = useMemo(() => {
    switch (currentStep) {
      case 'email': return t('authSheet.welcome', 'Welcome');
      case 'name': return t('authSheet.createAccount', 'Create your account');
      case 'otp': return t('authSheet.checkEmail', 'Check your email');
      case 'password': return t('authSheet.welcomeBack', 'Welcome back');
      default: return t('authSheet.welcome', 'Welcome');
    }
  }, [currentStep, t]);

  const headerSubtitle = useMemo(() => {
    // On name step, override any contextual reason with registration guidance
    if (currentStep === 'name') {
      return t('authSheet.finishSetup', 'Just a few details to get started');
    }
    // On other steps, show the contextual reason if provided
    return reason;
  }, [currentStep, reason, t]);

  // Auto-applied referral affordance — the captured `?ref=` code attributes on
  // signup automatically; we only surface a read-only chip when one is present.
  const referralCode = useMemo(
    () => (isOpen ? readStoredReferralCode() : null),
    [isOpen]
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const variants = useMemo(() => {
    if (isDesktop) {
      return {
        hidden: { opacity: 0, scale: 0.98, y: 6 },
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] }
        },
        exit: {
          opacity: 0,
          scale: 0.98,
          y: 6,
          transition: { duration: 0.16, ease: [0.4, 0, 1, 1] }
        }
      } as const;
    }

    return {
      hidden: { y: '100%', opacity: 0.98 },
      visible: { y: 0, opacity: 1, transition: SHEET_SPRING },
      exit: {
        y: '100%',
        opacity: 0.98,
        transition: { duration: 0.22, ease: [0.4, 0, 1, 1] }
      }
    } as const;
  }, [isDesktop]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-70 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Centering wrapper: ensures modal stays fully visible with breathing room */}
          <div
            className={cn(
              'fixed z-80 flex w-full',
              // Mobile: bottom sheet anchored to bottom
              'inset-x-0 bottom-0 items-end justify-center',
              // Desktop: centered with padding
              'sm:inset-0 sm:items-center sm:justify-center sm:p-4 sm:pb-6'
            )}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={headerTitle}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={variants}
              className={cn(
                'flex w-full flex-col bg-white shadow-2xl',
                'sm:max-w-[460px] sm:rounded-3xl sm:border sm:border-black/5',
                // Mobile: bottom sheet, minimal gap for safe area
                'max-h-[calc(100dvh-1rem)] rounded-t-3xl',
                // Desktop: cap height with slim viewport margin for consent step
                'sm:max-h-[calc(100dvh-1.5rem)]'
              )}
            >
              {/* Header - adapts to current auth step */}
              <div className="flex shrink-0 items-center justify-between px-5 pb-3 pt-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{headerTitle}</p>
                  {headerSubtitle && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
                      {headerSubtitle}
                    </p>
                  )}
                  {referralCode && (
                    <span
                      className="mt-1.5 inline-flex max-w-full items-center gap-1 rounded-full border border-[#8B7355]/25 bg-[#8B7355]/10 px-2 py-0.5 text-[11px] font-medium text-[#8B7355]"
                      title={`Invited by ${referralCode}`}
                    >
                      <Gift className="h-3 w-3 shrink-0" aria-hidden="true" />
                      <span className="truncate">Invited by {referralCode}</span>
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  aria-label={t('multiMethod.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content - min-h-0 allows flex child to shrink and scroll */}
              <div className="safe-area-bottom min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-2">
                <MultiMethodLoginForm
                  variant="compact"
                  mode="overlay"
                  onAuthenticated={onAuthenticated}
                  onStepChange={handleStepChange}
                />
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AuthSheet;
